import { useMemo } from 'react'

import {
  buildMemberRetirementAges,
  type CalculationResult,
  Calculator,
  type FinancialRecord,
  flattenFinRecordTree,
  type MemberRetirementAges,
  type SubmissionInput,
  Taxer,
  useFinancialRecordsBuilder,
} from '#calculator'
import type { BuildFinancialRecordsFn } from '#calculator/useFinancialRecordsBuilder'
import {
  calculationResultToChartsPayload,
  type ChartPayload,
  type ChartSeries,
  emptyChartsPayload,
} from '#charts'
import type { ChartMilestone } from '#charts/types'
import { useShowProfileQuery } from '#profile/api'
import ProfileService from '#profile/ProfileService'
import {
  applyRecordOverrides,
  resolveEffectiveOverrides,
  scenarioActiveQuests,
  useListQuestsQuery,
  useListQuestTemplatesQuery,
} from '#quests'
import { useListSubmissionsQuery } from '#submissions/api'

import { adjustRecords } from './adjustRecords'
import { computeBaselines } from './computeBaselines'
import useAdjustments, {
  type AdjustmentSlots,
  type ChartBaselines,
} from './useAdjustments'
import type { UseAdjustmentsState } from './useAdjustmentsState'

export type ScenarioSources = {
  adjustments?: boolean
  quests?: boolean
}

export type UseFinancialProjectionArgs = {
  useState?: UseAdjustmentsState
  scenario?: ScenarioSources
}

const EMPTY_RESULTS: readonly CalculationResult[] = []

export type UseFinancialProjectionResult = {
  chartSeries: ChartSeries
  results: {
    baseline: readonly CalculationResult[]
    scenario: readonly CalculationResult[]
  }
  adjustments: {
    dirty: boolean
    slots: AdjustmentSlots | null
    resetAll: () => void
  }
  quests: {
    dirty: boolean
  }
  isLoading: boolean
}

type Run = {
  results: readonly CalculationResult[]
  profile: ProfileService
  retirementAge: number
}

/** Batch counterpart of `build(submission)` — flatten each submission's tree. */
const buildRecords = (
  build: BuildFinancialRecordsFn,
  submissions: readonly SubmissionInput[]
): readonly FinancialRecord[] =>
  submissions.flatMap(submission =>
    flattenFinRecordTree(build(submission).tree)
  )

const toPayload = (run: Run | null): ChartPayload => {
  if (!run || run.results.length === 0) return emptyChartsPayload
  const { results, profile, retirementAge } = run
  const startYear = profile.projectionStartYear
  const ageAtStart = profile.person.getAgeInTheEndOfYear(startYear)
  const retirementMilestone: ChartMilestone = {
    age: String(retirementAge),
    label: 'Retirement',
    kind: 'retirement',
  }
  const calculationPayload = calculationResultToChartsPayload(
    [...results] as CalculationResult[],
    startYear,
    ageAtStart
  )
  return {
    ...calculationPayload,
    milestones: [retirementMilestone, ...calculationPayload.milestones],
  }
}

const useFinancialProjection = ({
  useState,
  scenario: sources = {},
}: UseFinancialProjectionArgs = {}): UseFinancialProjectionResult => {
  const scenarioIncludesAdjustments = sources.adjustments === true
  const scenarioIncludesQuests = sources.quests === true

  const { data: profileDoc, isLoading: isLoadingProfile } =
    useShowProfileQuery('default')
  const { data: listSubmissions, isLoading: isLoadingSubmissions } =
    useListSubmissionsQuery()
  const { build: buildBaselineRecord, isLoading: isLoadingBuild } =
    useFinancialRecordsBuilder()
  const { data: listQuests, isLoading: isLoadingQuests } = useListQuestsQuery({
    enabled: scenarioIncludesQuests,
  })
  const { data: questTemplate, isLoading: isLoadingQuestTemplates } =
    useListQuestTemplatesQuery({ enabled: scenarioIncludesQuests })

  // Quest templates go into the scenario `SurveyCollection` via
  // `extraSurveys` so the builder can parse quest submissions the same
  // way it parses regular ones.
  const extraSurveys = useMemo(
    () => (questTemplate ? [questTemplate] : []),
    [questTemplate]
  )

  const isLoading =
    isLoadingProfile ||
    isLoadingSubmissions ||
    isLoadingBuild ||
    isLoadingQuests ||
    isLoadingQuestTemplates

  const profile = useMemo(
    () => (profileDoc ? new ProfileService(profileDoc) : null),
    [profileDoc]
  )

  const baselineRetirementAges = useMemo(
    () => (profile ? buildMemberRetirementAges(profile.allMembers) : null),
    [profile]
  )

  const records = useMemo<readonly FinancialRecord[] | null>(() => {
    if (!buildBaselineRecord || !listSubmissions) return null
    return buildRecords(buildBaselineRecord, listSubmissions)
  }, [buildBaselineRecord, listSubmissions])

  const taxer = useMemo(() => {
    if (!profile) return null
    return new Taxer(profile)
  }, [profile])

  const initCalculator = useMemo(() => {
    if (!taxer || !profile) return null
    return (
      records: readonly FinancialRecord[],
      retirementAges: MemberRetirementAges
    ): Calculator =>
      new Calculator(records, taxer, profile, [], true, retirementAges)
  }, [taxer, profile])

  // TODO: extract the calculation-run body into a private helper
  // `runCalculator(records, retirementAges): Run` and call it twice —
  // once here with the baseline inputs, once inside `scenarioRun` with the
  // scenario inputs. Right now the shape (init → calculate → package into
  // `Run`) is duplicated between the two memos.
  const baselineRun = useMemo<Run | null>(() => {
    if (!records || !profile || !initCalculator || !baselineRetirementAges)
      return null
    const calc = initCalculator(records, baselineRetirementAges)
    return {
      results: calc.calculate(),
      profile,
      retirementAge: profile.person.retirementAge,
    }
  }, [records, profile, initCalculator, baselineRetirementAges])

  // TODO: move inside the useAdjustments hook
  const baselines = useMemo<ChartBaselines | null>(() => {
    if (!baselineRun || baselineRun.results.length === 0) return null
    return computeBaselines(
      baselineRun.results,
      baselineRun.profile.person.retirementAge
    )
  }, [baselineRun])

  const { slots, adjustments, hasAdjustments, resetAll } = useAdjustments(
    baselines,
    useState,
    profile?.person.currentAge
  )

  const retirementAges = useMemo<MemberRetirementAges | null>(() => {
    if (!profile || !baselineRetirementAges) return null
    const override = scenarioIncludesAdjustments
      ? adjustments.retirementAge
      : undefined
    return override === undefined
      ? baselineRetirementAges
      : { ...baselineRetirementAges, [profile.person.id]: override }
  }, [
    scenarioIncludesAdjustments,
    profile,
    baselineRetirementAges,
    adjustments.retirementAge,
  ])

  const { build: buildScenarioRecord } = useFinancialRecordsBuilder({
    extraSurveys,
    retirementAges: retirementAges ?? undefined,
  })

  const questRecords = useMemo<readonly FinancialRecord[]>(() => {
    if (!buildScenarioRecord) return []
    const enabledQuests = scenarioActiveQuests(
      scenarioIncludesQuests,
      listQuests
    )
    if (enabledQuests.length === 0) return []
    // TODO(ZL-815): quest-to-quest overrides are REACHABLE but unspecified. Quest
    // docs are submissions, so a quest's root record id lives in the same key
    // space as a plan record's, and applying the layer here means one quest can
    // override another's record if an override is ever written against that id.
    // Nothing in the UI offers it — `buildTuningCandidates` enumerates plan
    // submissions only, and a quest's own root is `catTechnicalQuestScenario` —
    // so it cannot happen by accident today. Decide whether to specify it or
    // forbid it outright (filtering on `record.questId == null` would do it)
    // before anything starts writing such overrides.
    const resolved = resolveEffectiveOverrides(enabledQuests)
    return applyRecordOverrides(
      buildRecords(buildScenarioRecord, enabledQuests),
      resolved
    )
  }, [scenarioIncludesQuests, buildScenarioRecord, listQuests])

  const scenarioRun = useMemo<Run | null>(() => {
    // Skip the second Calculator run when no scenario ingredient actually changes
    // anything — either adjustments are off/clean or there are no quests to fold.
    const hasActiveAdjustments = scenarioIncludesAdjustments && hasAdjustments
    const hasActiveQuests = scenarioIncludesQuests && questRecords.length > 0
    if (!hasActiveAdjustments && !hasActiveQuests) return null
    if (
      !records ||
      !profile ||
      !initCalculator ||
      !listSubmissions ||
      !buildScenarioRecord ||
      !retirementAges ||
      !baselineRetirementAges
    )
      return null
    // When any enabled quest carries tunings targeting a base submission, the
    // scenario base records must reflect the merged answers even if retirement
    // ages haven't changed. Otherwise the retirement-unchanged fast-path can
    // reuse the pristine baseline records. `scenarioActiveQuests` returns []
    // when the scenario ingredient is off, so cached `listQuests` from a
    // prior enabled run can't leak tunings into disabled-quests runs.
    const enabledQuests = scenarioActiveQuests(
      scenarioIncludesQuests,
      listQuests
    )
    // One resolve for both halves: which records the quests turn off, and which
    // parsed values they override. Most recent edit wins — see
    // resolveEffectiveOverrides. Records a quest turns off are dropped from the
    // scenario only; the baseline run above still builds them, so they stay in the
    // Plan and only the quest's projection ignores them.
    const resolved = resolveEffectiveOverrides(enabledQuests)
    // Only muting needs a rebuild, because it removes a submission before the
    // build. Value overrides are applied to already-built records, so they no
    // longer force one — which is the practical payoff of moving off answer
    // substitution (ZL-815).
    const hasMutes = resolved.mutes.size > 0
    const scenarioBaseRecords =
      retirementAges === baselineRetirementAges && !hasMutes
        ? records
        : buildRecords(
            buildScenarioRecord,
            hasMutes
              ? listSubmissions.filter(s => !resolved.mutes.has(s.id))
              : listSubmissions
          )
    // Before the What-If multipliers, matching the old ordering: a quest pins an
    // absolute figure and the multiplier then scales it, rather than the override
    // discarding the user's slider.
    const scenarioSubmissionRecords = applyRecordOverrides(
      scenarioBaseRecords,
      resolved
    )
    const adjustedSubmissionRecords = hasActiveAdjustments
      ? adjustRecords(scenarioSubmissionRecords, adjustments)
      : scenarioSubmissionRecords
    const scenarioRecords = [...adjustedSubmissionRecords, ...questRecords]
    const calc = initCalculator(scenarioRecords, retirementAges)
    return {
      results: calc.calculate(),
      profile,
      retirementAge: retirementAges[profile.person.id],
    }
  }, [
    scenarioIncludesAdjustments,
    scenarioIncludesQuests,
    hasAdjustments,
    adjustments,
    records,
    profile,
    initCalculator,
    listSubmissions,
    listQuests,
    buildScenarioRecord,
    retirementAges,
    baselineRetirementAges,
    questRecords,
  ])

  const baselinePayload = useMemo(() => toPayload(baselineRun), [baselineRun])
  const scenarioPayload = useMemo(() => toPayload(scenarioRun), [scenarioRun])

  const baseline = baselinePayload
  const scenario = scenarioRun !== null ? scenarioPayload : baselinePayload
  // Per-year detail the chart payload aggregates away — `taxResult` and the
  // record timeline. Same baseline/scenario fallback as the payloads, so a
  // consumer showing one cannot accidentally read the other.
  const baselineResults = baselineRun?.results ?? EMPTY_RESULTS
  const scenarioResults = scenarioRun?.results ?? baselineResults
  const adjustmentsDirty = scenarioIncludesAdjustments && hasAdjustments
  const questsDirty = scenarioIncludesQuests && questRecords.length > 0

  return useMemo(
    () => ({
      chartSeries: { baseline, scenario },
      results: { baseline: baselineResults, scenario: scenarioResults },
      adjustments: { dirty: adjustmentsDirty, slots, resetAll },
      quests: { dirty: questsDirty },
      isLoading,
    }),
    [
      baseline,
      scenario,
      baselineResults,
      scenarioResults,
      adjustmentsDirty,
      slots,
      resetAll,
      questsDirty,
      isLoading,
    ]
  )
}

export default useFinancialProjection
